import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { initialUnslotVariablesState } from "./unslotVariablesState";
import { IState } from "slices";
import { CreateUnslotSpecCommand, IUnslotSpecsClient, IVariablesClient, UnslotSpecsClient, VariablesClient } from "app/ApiClient";
import { LoadingState } from "types";
import isEqual from "lodash/isEqual";
const variablesClient: IVariablesClient = new VariablesClient(process.env.REACT_APP_API_URL);
const unslotSpecsClient: IUnslotSpecsClient = new UnslotSpecsClient(process.env.REACT_APP_API_URL);

export const getVariableBrands = createAsyncThunk(
  "variables/getVariableBrands",
  async ({ variable }: { variable: string; variableIndex:number; }, { rejectWithValue, getState },
  ) => {
    try {
      const {
        project: { currentProject },
        datasetsData: { currentVersionId, currentStackId, currentModificationId },
      } = getState() as IState;
      if(!variable) {
        return [];
      }
      if(!(currentProject?.id)) {
        return;
      }
      const result = await variablesClient.getCategories(
          currentProject.id,
          currentVersionId, 
          variable, 
          currentModificationId, 
          currentStackId
      );
      return result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const createUnslotSpecs = createAsyncThunk(
  "unslotSpecs/createUnslotSpec",
  async (data: CreateUnslotSpecCommand, { rejectWithValue }) => {
    try {
      const response = await unslotSpecsClient.createUnslotSpecs({ ...data });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getUnslotSpecHistory = createAsyncThunk(
  "unslotSpecs/GetUnslotReportHistory",
  async ({projectid, version}: {projectid:number, version:string}, { rejectWithValue }) => {
    try {
      const response = await unslotSpecsClient.getUnslotReportHistory(projectid, version);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getUnslotStatusByToken = createAsyncThunk(
  "unslotSpecs/getUnslotStatus",
  async (token: string, { rejectWithValue }) => {
    try {
      const response = await unslotSpecsClient.getUnslotStatus(token);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getUnslotStatusByProject = createAsyncThunk(
  "unslotSpecs/getUnslotStatusByProject",
  async ({projectid, version}: {projectid:number, version:string}, { rejectWithValue }) => {
    try {
      const response = await unslotSpecsClient.getUnslotStatusByProject(projectid, version);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getUnslotSpecsBySpecName = createAsyncThunk(
  "unslotSpecs/getUnslotFormData",
  async ({projectid, version, specName}: {projectid:number, version:string, specName:string}, { rejectWithValue }) => {
    try {
      const response = await unslotSpecsClient.getUnslotFormData(projectid, version, specName);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const unslotVariablesSlice = createSlice({
  name: "unslotVariables",
  initialState: initialUnslotVariablesState,
  reducers: {
    resetUnslotVariables: () => initialUnslotVariablesState,
    setUnslotVariablesRecordDialogOpended: (state, { payload }: PayloadAction<boolean>) => {
      state.isUnslotVariablesRecordDialogOpended = payload;
    },
    setUnslotVariablesDialogOpended: (state, { payload }: PayloadAction<boolean>) => {
      state.isUnslotVariablesDialogOpended = payload;
    },
    setMinimiseDialog: (state, { payload }: PayloadAction<boolean>) => {
      state.isMinimiseDialog = payload;
    },
    validateVariablesCategories:(state, { payload }: PayloadAction<number>) => {
      if(Object.keys(state.variablesCategories).length > payload) {
        const categoryEntries = Object.entries(state.variablesCategories).slice(0, payload);
        state.variablesCategories = Object.fromEntries(categoryEntries);
      }
    },
    validateBrands:((state, {payload}:PayloadAction<number>) => {
      if(Object.keys(state.variablesCategories).length !== payload) {
        state.validBrands=undefined;
        state.brands = [];
        return;
      }
      const variableCategoriesRecords = Object.values(state.variablesCategories);
      const firstVariableCategories = variableCategoriesRecords[0].categories ?? [];
      const firstVariableCategoriesValues = firstVariableCategories?.map(x=>x.value)?.sort() ?? [];
      state.validBrands = variableCategoriesRecords.every(value=>isEqual(value.categories?.map(x=>x.value).sort(), firstVariableCategoriesValues));
      state.brands = state.validBrands ? [...firstVariableCategories] : [];        
    }),
    setSelectedUnslotSpecName: (state, { payload }: PayloadAction<string>) => {
      state.selectedUnslotSpecName = payload;
    },
  },
  extraReducers: (builder) => {
    // getVariableBrands
    builder.addCase(getVariableBrands.pending, (state, { meta:  { arg : { variableIndex } }}) => {
      state.variablesCategories[variableIndex] = { loadingState : LoadingState.pending };
    });
    builder.addCase(getVariableBrands.fulfilled, (state, { payload, meta:  { arg : { variableIndex } }}) => {
      state.variablesCategories[variableIndex] = { loadingState : LoadingState.succeed, categories:payload };
    });
    builder.addCase(getVariableBrands.rejected, (state, { meta:  { arg : { variableIndex} }}) => {
      state.variablesCategories[variableIndex] = { loadingState : LoadingState.failed };
    });

    // create unslot
    builder.addCase(createUnslotSpecs.pending, (state) => {
      state.createUnslotSpecState = LoadingState.pending;
      state.UnslotSpec = null;
    });
    builder.addCase(createUnslotSpecs.fulfilled, (state, { payload }) => {
      state.createUnslotSpecState = payload.token?LoadingState.succeed:LoadingState.failed;
      state.UnslotSpec = payload;
    });
    builder.addCase(createUnslotSpecs.rejected, (state) => {
      state.createUnslotSpecState = LoadingState.failed;
    });
    // get unslot spec history
    builder.addCase(getUnslotSpecHistory.pending, (state) => {
      state.unslotSpecHistoriesState = LoadingState.pending;
    });
    builder.addCase(getUnslotSpecHistory.fulfilled, (state, { payload }) => {
      state.unslotSpecHistoriesState = LoadingState.succeed;
      state.UnslotSpecHistories = payload;
    });
    builder.addCase(getUnslotSpecHistory.rejected, (state) => {
      state.unslotSpecHistoriesState = LoadingState.failed;
    });
    // get unslot status by project
    builder.addCase(getUnslotStatusByProject.pending, (state) => {
      state.unslotSpecStatusState = LoadingState.pending;
    });
    builder.addCase(getUnslotStatusByProject.fulfilled, (state, { payload }) => {
      state.unslotSpecStatusState = LoadingState.succeed;
      state.unslotSpecStatus = payload;
    });
    builder.addCase(getUnslotStatusByProject.rejected, (state) => {
      state.unslotSpecStatusState = LoadingState.failed;
    });
    // get unslot status by token
    builder.addCase(getUnslotStatusByToken.pending, (state) => {
      state.unslotPollingState = LoadingState.pending;
    });
    builder.addCase(getUnslotStatusByToken.fulfilled, (state, { payload }) => {
      state.unslotPollingState = LoadingState.succeed;
      state.unslotSpecStatus = payload;
    });
    builder.addCase(getUnslotStatusByToken.rejected, (state) => {
      state.unslotPollingState = LoadingState.failed;
    });
    // get unslot status by token
    builder.addCase(getUnslotSpecsBySpecName.pending, (state) => {
      state.unslotSpecsFormDataState = LoadingState.pending;
    });
    builder.addCase(getUnslotSpecsBySpecName.fulfilled, (state, { payload }) => {
      state.unslotSpecsFormDataState = LoadingState.succeed;
      state.unslotSpecsFormData = payload;
    });
    builder.addCase(getUnslotSpecsBySpecName.rejected, (state) => {
      state.unslotSpecsFormDataState = LoadingState.failed;
    });
  }
});

export const {
  resetUnslotVariables,
  setUnslotVariablesDialogOpended,
  setMinimiseDialog,
  validateBrands,
  validateVariablesCategories,
  setUnslotVariablesRecordDialogOpended,
  setSelectedUnslotSpecName,
} = unslotVariablesSlice.actions;
export const unslotVariablesSelector = (state: IState) => state.unslotVariables;
export default unslotVariablesSlice.reducer;
